// src/pages/freevaluation/FreeValuationForm.jsx
//
// Post-magic-link submission form. User is already authenticated
// (session cookie set by the /verify redirect). Collects: org
// number, contact name, SIE file. On submit → hits the backend
// pipeline endpoint → redirects to the "processing" screen that
// polls status.
//
// Route: /gratis-vardering/formular (session-gated by the backend
// submit endpoint; the frontend route itself is public — an
// unauthenticated visitor just hits a 401 on submit).

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { request, API_BASE } from "../../services/server";
import "./freevaluation.css";

const _MAX_MB = 20;

function _digitsOnly(s) {
  return (s || "").replace(/\D/g, "");
}

export default function FreeValuationForm() {
  const navigate = useNavigate();
  const [orgNumber, setOrgNumber] = useState("");
  const [contactName, setContactName] = useState("");
  const [sieFile, setSieFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [utm, setUtm] = useState({});

  useEffect(() => {
    // The verify endpoint passes utm as a query param; we picked it
    // up from the URL and threaded through to the form. Not
    // load-bearing — utm is a nice-to-have for marketing attribution.
    const params = new URLSearchParams(window.location.search);
    const utmParam = params.get("utm");
    if (utmParam) {
      try {
        setUtm(JSON.parse(utmParam.replace(/'/g, '"')));
      } catch {
        // Ignore malformed utm — attribution is best-effort.
      }
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const cleanOrg = _digitsOnly(orgNumber);
    if (cleanOrg.length !== 10) {
      setError("Organisationsnumret ska vara 10 siffror.");
      return;
    }
    if (!contactName.trim()) {
      setError("Ange ditt namn.");
      return;
    }
    if (!sieFile) {
      setError("Ladda upp en SIE-fil från ditt bokföringssystem.");
      return;
    }
    if (sieFile.size > _MAX_MB * 1024 * 1024) {
      setError(`SIE-filen är för stor (max ${_MAX_MB} MB).`);
      return;
    }

    setStatus("submitting");
    const form = new FormData();
    form.append("org_number", cleanOrg);
    form.append("contact_name", contactName.trim());
    form.append("sie_file", sieFile);
    if (Object.keys(utm).length > 0) {
      form.append("utm", JSON.stringify(utm));
    }

    try {
      const result = await request(
        "/valuation/free-valuation/submit/",
        { method: "POST", body: form, formData: true }
      );
      if (!result?.ok) {
        setError(result?.error || "Något gick fel.");
        setStatus("idle");
        return;
      }
      navigate(`/gratis-vardering/behandlas/${result.workspace_id}`);
    } catch (err) {
      // 400 responses from the backend carry the Swedish error
      // message in the body — surface it verbatim.
      const bodyMsg = err?.data?.error || err?.body?.error;
      setError(
        bodyMsg ||
          err?.message ||
          "Något gick fel när värderingen skulle beställas. Försök igen."
      );
      setStatus("idle");
    }
  };

  return (
    <div className="fv-page">
      <div className="fv-card fv-card-wide">
        <h1>Sista steget — ladda upp din SIE-fil</h1>
        <p className="fv-lead">
          Exportera en SIE-4 fil från ditt bokföringssystem (Fortnox,
          Visma, Björn Lundén m.fl.). Filen ska omfatta minst två
          räkenskapsår.
        </p>

        <form onSubmit={onSubmit} className="fv-form">
          <label className="fv-label">
            Organisationsnummer
            <input
              type="text"
              required
              value={orgNumber}
              onChange={(e) => setOrgNumber(e.target.value)}
              placeholder="556010-1008"
              autoComplete="off"
              disabled={status === "submitting"}
            />
          </label>

          <label className="fv-label">
            Ditt namn
            <input
              type="text"
              required
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="För- och efternamn"
              autoComplete="name"
              disabled={status === "submitting"}
            />
          </label>

          <label className="fv-label">
            SIE-fil
            <input
              type="file"
              accept=".se,.si,.sie,text/plain,application/octet-stream"
              required
              onChange={(e) => setSieFile(e.target.files?.[0] || null)}
              disabled={status === "submitting"}
            />
            <span className="fv-help">
              Så här exporterar du SIE:{" "}
              <a
                href="https://support.fortnox.se/artikel/Exportera-SIE-fil-fran-Fortnox"
                target="_blank"
                rel="noopener noreferrer"
              >
                Fortnox
              </a>{" "}
              ·{" "}
              <a
                href="https://vismaspcs.se/support"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visma
              </a>
            </span>
          </label>

          <button
            type="submit"
            className="fv-primary"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? "Laddar upp…" : "Få min värdering"}
          </button>

          {error && <p className="fv-error">{error}</p>}
        </form>
      </div>
    </div>
  );
}
